import React, { useState, useEffect, useCallback } from 'react';
import { Music2, Play, Pause, ShoppingCart, Check } from 'lucide-react';
import axios from 'axios';
import { useBeatsCart } from '../contexts/BeatsCartContext';
import { useAuth } from '../contexts/AuthContext';
import TierBadge from '../components/TierBadge';
import BeatsHeaderAd from '../components/ads/BeatsHeaderAd';

const API = process.env.REACT_APP_BACKEND_URL;

const BeatsPage = () => {
  const [beats, setBeats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [playingBeat, setPlayingBeat] = useState(null);
  const [audioElement, setAudioElement] = useState(null);
  const { addBeat, isInCart, openCart } = useBeatsCart();
  const { user } = useAuth();

  const fetchBeats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/api/beats`);
      setBeats(response.data);
    } catch (error) {
      console.error('Failed to load beats', error);
    } finally {
      setLoading(false);
    }
  }, []); // API and axios are stable

  useEffect(() => {
    fetchBeats();
  }, [fetchBeats]);

  useEffect(() => {
    return () => {
      if (audioElement) {
        audioElement.pause();
        audioElement.src = '';
      }
    };
  }, [audioElement]);

  const handlePlayPause = (beat) => {
    if (!beat.audio_url) return;

    if (playingBeat?.id === beat.id) {
      // Pause current
      if (audioElement) {
        audioElement.pause();
      }
      setPlayingBeat(null);
    } else {
      // Play new beat
      if (audioElement) {
        audioElement.pause();
      }
      // Construct full URL for audio (handles both relative and absolute URLs)
      const audioUrl = beat.audio_url.startsWith('http') 
        ? beat.audio_url 
        : `${API}${beat.audio_url}`;
      
      const audio = new Audio(audioUrl);
      audio.play();
      audio.onended = () => setPlayingBeat(null);
      setAudioElement(audio);
      setPlayingBeat(beat);
    }
  };

  const handleAddToCart = (beat, licenseType) => {
    addBeat(beat, licenseType);
  };

  const getLicenseDescription = (type) => {
    const descriptions = {
      basic_lease: 'Non-exclusive • Limited distribution • 50% royalties',
      premium_lease: 'Non-exclusive • Unlimited distribution • 25% royalties',
      exclusive_rights: 'Exclusive use • Full commercial rights • 0% royalties'
    };
    return descriptions[type] || '';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0D0D0D] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0D0D0D] pt-24 pb-32">
      <BeatsHeaderAd />
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        {/* Header */}
        <div className="mb-12">
          {user?.subscription_tier && (
            <div className="mb-4">
              <TierBadge tier={user.subscription_tier} />
            </div>
          )}
          <p className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] mb-2">
            License Production Beats
          </p>
          <h1 className="font-['Bebas_Neue'] text-5xl md:text-7xl mb-4">
            BEATS STORE
          </h1>
          <p className="text-lg text-[#A3A3A3] max-w-2xl">
            High-quality production beats available for licensing. Choose the license type that fits your needs.
          </p>
        </div>

        {/* Beats Grid */}
        {beats.length === 0 ? (
          <div className="text-center py-20">
            <Music2 size={64} className="mx-auto text-[#D4AF37]/30 mb-4" />
            <p className="text-xl text-[#A3A3A3]">No beats available yet</p>
            <p className="text-sm text-[#666] mt-2">Check back soon for new production beats</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {beats.map((beat) => (
              <div
                key={beat.id}
                className="bg-[#1A1A1A] rounded-lg overflow-hidden border border-[#262626] hover:border-[#D4AF37]/30 transition-all"
              >
                {/* Beat Header */}
                <div className="p-6 border-b border-[#262626]">
                  <div className="flex gap-4">
                    {/* Cover Art */}
                    <div className="w-20 h-20 bg-[#262626] rounded overflow-hidden flex-shrink-0 relative group">
                      {beat.cover_art_url ? (
                        <img
                          src={beat.cover_art_url}
                          alt={beat.title}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Music2 size={32} className="text-[#D4AF37]/50" />
                        </div>
                      )}
                      {beat.audio_url && (
                        <button
                          onClick={() => handlePlayPause(beat)}
                          className="absolute inset-0 bg-black/60 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          {playingBeat?.id === beat.id ? (
                            <Pause size={24} className="text-[#D4AF37]" />
                          ) : (
                            <Play size={24} className="text-[#D4AF37]" />
                          )}
                        </button>
                      )}
                    </div>

                    {/* Beat Info */}
                    <div className="flex-1">
                      <h3 className="font-['Bebas_Neue'] text-2xl text-white mb-1">
                        {beat.title}
                      </h3>
                      <div className="flex flex-wrap gap-3 text-sm text-[#A3A3A3]">
                        {beat.bpm && <span>{beat.bpm} BPM</span>}
                        {beat.key && <span>• {beat.key}</span>}
                        {beat.genre && <span>• {beat.genre}</span>}
                      </div>
                      {beat.mood && (
                        <p className="text-xs text-[#D4AF37] uppercase tracking-wider mt-2">
                          {beat.mood}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* License Options */}
                <div className="p-6 space-y-3">
                  {/* Basic Lease */}
                  <div className="flex items-center justify-between p-4 bg-[#0D0D0D] rounded border border-[#262626]">
                    <div className="flex-1">
                      <p className="font-medium text-white mb-1">Basic Lease</p>
                      <p className="text-xs text-[#A3A3A3]">
                        {getLicenseDescription('basic_lease')}
                      </p>
                    </div>
                    <div className="flex items-center gap-3 ml-4">
                      <span className="font-['Bebas_Neue'] text-xl text-[#D4AF37]">
                        ${beat.basic_lease_price?.toFixed(2) || '29.99'}
                      </span>
                      <button
                        onClick={() => handleAddToCart(beat, 'basic_lease')}
                        className={`p-2 rounded transition-colors ${
                          isInCart(beat.id, 'basic_lease')
                            ? 'bg-green-600 text-white'
                            : 'bg-[#D4AF37] text-black hover:bg-[#E5C048]'
                        }`}
                        title={isInCart(beat.id, 'basic_lease') ? 'In cart' : 'Add to cart'}
                      >
                        {isInCart(beat.id, 'basic_lease') ? (
                          <Check size={18} />
                        ) : (
                          <ShoppingCart size={18} />
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Premium Lease */}
                  <div className="flex items-center justify-between p-4 bg-[#0D0D0D] rounded border border-[#262626]">
                    <div className="flex-1">
                      <p className="font-medium text-white mb-1">Premium Lease</p>
                      <p className="text-xs text-[#A3A3A3]">
                        {getLicenseDescription('premium_lease')}
                      </p>
                    </div>
                    <div className="flex items-center gap-3 ml-4">
                      <span className="font-['Bebas_Neue'] text-xl text-[#D4AF37]">
                        ${beat.premium_lease_price?.toFixed(2) || '99.99'}
                      </span>
                      <button
                        onClick={() => handleAddToCart(beat, 'premium_lease')}
                        className={`p-2 rounded transition-colors ${
                          isInCart(beat.id, 'premium_lease')
                            ? 'bg-green-600 text-white'
                            : 'bg-[#D4AF37] text-black hover:bg-[#E5C048]'
                        }`}
                        title={isInCart(beat.id, 'premium_lease') ? 'In cart' : 'Add to cart'}
                      >
                        {isInCart(beat.id, 'premium_lease') ? (
                          <Check size={18} />
                        ) : (
                          <ShoppingCart size={18} />
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Exclusive Rights */}
                  <div className="flex items-center justify-between p-4 bg-[#0D0D0D] rounded border border-[#D4AF37]/30">
                    <div className="flex-1">
                      <p className="font-medium text-white mb-1 flex items-center gap-2">
                        Exclusive Rights
                        <span className="text-[10px] bg-[#D4AF37] text-black px-2 py-0.5 rounded">
                          BEST VALUE
                        </span>
                      </p>
                      <p className="text-xs text-[#A3A3A3]">
                        {getLicenseDescription('exclusive_rights')}
                      </p>
                    </div>
                    <div className="flex items-center gap-3 ml-4">
                      <span className="font-['Bebas_Neue'] text-xl text-[#D4AF37]">
                        ${beat.exclusive_rights_price?.toFixed(2) || '299.99'}
                      </span>
                      <button
                        onClick={() => handleAddToCart(beat, 'exclusive_rights')}
                        className={`p-2 rounded transition-colors ${
                          isInCart(beat.id, 'exclusive_rights')
                            ? 'bg-green-600 text-white'
                            : 'bg-[#D4AF37] text-black hover:bg-[#E5C048]'
                        }`}
                        title={isInCart(beat.id, 'exclusive_rights') ? 'In cart' : 'Add to cart'}
                      >
                        {isInCart(beat.id, 'exclusive_rights') ? (
                          <Check size={18} />
                        ) : (
                          <ShoppingCart size={18} />
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* View Cart CTA */}
        {beats.length > 0 && (
          <div className="mt-12 text-center">
            <button
              onClick={openCart}
              className="px-8 py-4 bg-[#D4AF37] text-black font-medium uppercase tracking-wider hover:bg-[#E5C048] transition-colors inline-flex items-center gap-2"
            >
              <ShoppingCart size={20} />
              View Cart
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default BeatsPage;
